import { Injectable } from '@angular/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { ReportRequest } from '../dto/report-request';
import { environment } from '../../../../environments/environment.development';


export interface ReportProgress { percent: number; stage: string; }
export interface ReportCompleted { fileId: string; downloadUrl: string; fileName: string; }

export interface StreamHandlers {
  onProgress: (p: ReportProgress) => void;
  onCompleted: (c: ReportCompleted) => void;
  onFailed: (message: string) => void;
  onUnauthorized?: () => void;
}

@Injectable({ providedIn: 'root' })
export class ReportStreamService {

  /**
   * 发起长任务并监听 SSE 进度。返回 AbortController 供外部取消。
   */
  generateStream(reportRequest: ReportRequest, handlers: StreamHandlers): AbortController {
    const ctrl = new AbortController();
    let finished = false; // 收到 completed/error 后不再判定为"意外中断"
    const token = localStorage.getItem(environment.jwtToken);

    fetchEventSource(environment.apiRoot + '/sse/report', {
      method: 'POST',
      body: JSON.stringify(reportRequest),
      signal: ctrl.signal,
      openWhenHidden: true, // 关键：切后台标签页时不断开（默认会 abort）
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },

      async onopen(res) {
        const type = res.headers.get('content-type') ?? '';
        if (res.ok && type.includes('text/event-stream')) return;
        if (res.status === 401 || res.status === 403) {
          finished = true;
          handlers.onUnauthorized?.();
          throw new Error('unauthorized');
        }
        finished = true;
        throw new Error(`服务连接失败 (HTTP ${res.status})`);
      },

      onmessage(ev) {
        const data = JSON.parse(ev.data);
        switch (ev.event) {
          case 'progress':
            handlers.onProgress(data as ReportProgress);
            break;
          case 'completed':
            finished = true;
            handlers.onCompleted(data as ReportCompleted);
            break;
          case 'error':
            finished = true;
            handlers.onFailed(data.message);
            break;
        }
      },

      onclose() {
        // 服务端提前关闭流且没收到终态事件 → 视为异常
        if (!finished) handlers.onFailed('连接意外中断');
      },

      // 关键：抛出错误终止库的自动重连。长任务重连没有意义（后台任务不重复跑），
      // 失败就明确告诉用户
      onerror(err) {
        finished = true;
        throw err;
      },
    }).catch((err) => {
      if (err?.name === 'AbortError') return; // 主动取消不算失败
      if (!finished) handlers.onFailed(err?.message ?? '连接中断');
    });

    return ctrl;
  }

}